import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, RefreshCw, FileSignature, X, Sparkles, FileText, Package, AlertTriangle, Euro } from 'lucide-react';
import { toast } from 'sonner';
import { AccontoSelector } from '@/components/AccontoSelector';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
const parseItNum = (val) => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  let s = String(val).trim();
  if (s.includes(',')) {
    // Formato italiano: punti = migliaia, virgola = decimali
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    const dots = (s.match(/\./g) || []).length;
    // Un solo punto seguito da 1-2 cifre finali → separatore decimale (es. "28776.65")
    if (!(dots === 1 && /\.\d{1,2}$/.test(s))) {
      s = s.replace(/\./g, '');
    }
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const TipoBadge = ({ tipo }) => tipo === 'rapportino'
  ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px]"><FileText className="h-3 w-3 mr-1" />Rapportino</Badge>
  : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[11px]"><Package className="h-3 w-3 mr-1" />DDT</Badge>;

export const AccontiRiepilogo = ({ cantiereId, onContoCreato }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(new Set());
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [assignTarget, setAssignTarget] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [contoDialogOpen, setContoDialogOpen] = useState(false);
  const [contoBlock, setContoBlock] = useState(null);
  const [contoForm, setContoForm] = useState({ descrizione: '', imponibile: '', iva_pct: '0' });
  const [creatingConto, setCreatingConto] = useState(false);
  const [ricEdits, setRicEdits] = useState({});
  const [ricBusy, setRicBusy] = useState(new Set());
  const autoRegenDone = useRef(new Set());

  const regenerateBlock = useCallback(async (rif) => {
    setRegenerating(prev => new Set(prev).add(rif));
    try {
      const r = await axios.post(`${API}/cantieri/${cantiereId}/acconti/${rif}/genera-descrizione`, {}, { withCredentials: true });
      setData(prev => prev ? {
        ...prev,
        blocchi: prev.blocchi.map(b => b.riferimento === rif
          ? { ...b, descrizione_ai: r.data.descrizione, oggetto_ai: r.data.oggetto, ai_updated_at: r.data.updated_at, ai_stale: false }
          : b),
      } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore generazione descrizione AI');
    } finally {
      setRegenerating(prev => { const s = new Set(prev); s.delete(rif); return s; });
    }
  }, [cantiereId]);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cantieri/${cantiereId}/acconti/riepilogo`, { withCredentials: true });
      setData(r.data);
      setRicEdits(prev => {
        const next = { ...prev };
        (r.data.blocchi || []).forEach(b => {
          if (!next[b.riferimento]) {
            next[b.riferimento] = {
              mano: String(b.ricarico_manodopera_pct || 0).replace('.', ','),
              mat: String(b.ricarico_materiali_pct || 0).replace('.', ','),
            };
          }
        });
        return next;
      });
      // Auto-rigenera le descrizioni AI dei blocchi cambiati (una volta per hash)
      (r.data.blocchi || []).forEach(b => {
        if (b.ai_stale && !autoRegenDone.current.has(b.docs_hash)) {
          autoRegenDone.current.add(b.docs_hash);
          regenerateBlock(b.riferimento);
        }
      });
    } catch {
      toast.error('Errore caricamento riepilogo acconti');
    } finally {
      setLoading(false);
    }
  }, [cantiereId, regenerateBlock]);

  useEffect(() => { setLoading(true); setSelectedDocs(new Set()); fetchData(); }, [fetchData]);

  const toggleDoc = (tipo, id) => {
    const key = `${tipo}|${id}`;
    setSelectedDocs(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const assignSelected = async () => {
    if (!assignTarget) { toast.error('Indica Acconto n° oppure Extra'); return; }
    if (selectedDocs.size === 0) { toast.error('Seleziona almeno un documento'); return; }
    setAssignBusy(true);
    try {
      const items = Array.from(selectedDocs).map(k => { const [tipo, id] = k.split('|'); return { tipo, id }; });
      await axios.put(`${API}/cantieri/${cantiereId}/acconti/assegna`, { items, acconto_riferimento: assignTarget }, { withCredentials: true });
      toast.success(`${items.length} documenti assegnati`);
      setSelectedDocs(new Set());
      setAssignTarget('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore assegnazione');
    } finally {
      setAssignBusy(false);
    }
  };

  const unassignDoc = async (tipo, id) => {
    try {
      await axios.put(`${API}/cantieri/${cantiereId}/acconti/assegna`, { items: [{ tipo, id }], acconto_riferimento: null }, { withCredentials: true });
      toast.success('Documento rimosso dal blocco');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore rimozione');
    }
  };

  const saveRicarico = async (b) => {
    const e = ricEdits[b.riferimento] || {};
    setRicBusy(prev => new Set(prev).add(b.riferimento));
    try {
      await axios.put(`${API}/cantieri/${cantiereId}/acconti/${b.riferimento}/ricarico`, {
        ricarico_manodopera_pct: parseItNum(e.mano),
        ricarico_materiali_pct: parseItNum(e.mat),
        esclusi: b.esclusi || [],
      }, { withCredentials: true });
      toast.success('Ricarico del blocco aggiornato');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio ricarico');
    } finally {
      setRicBusy(prev => { const s = new Set(prev); s.delete(b.riferimento); return s; });
    }
  };

  const toggleEscluso = async (b, d) => {
    const key = `${d.tipo}|${d.id}`;
    const esclusi = new Set(b.esclusi || []);
    if (esclusi.has(key)) esclusi.delete(key); else esclusi.add(key);
    try {
      await axios.put(`${API}/cantieri/${cantiereId}/acconti/${b.riferimento}/ricarico`, {
        ricarico_manodopera_pct: b.ricarico_manodopera_pct || 0,
        ricarico_materiali_pct: b.ricarico_materiali_pct || 0,
        esclusi: Array.from(esclusi),
      }, { withCredentials: true });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento ricarico');
    }
  };

  const openContoDialog = (block) => {
    setContoBlock(block);
    setContoForm({ descrizione: block.descrizione_ai || '', imponibile: (block.totale || 0).toFixed(2).replace('.', ','), iva_pct: '0' });
    setContoDialogOpen(true);
  };

  const handleCreaConto = async () => {
    setCreatingConto(true);
    try {
      const r = await axios.post(`${API}/cantieri/${cantiereId}/conti-emessi`, {
        descrizione: contoForm.descrizione,
        prezzo_calcolato: contoBlock.totale,
        prezzo_emesso: parseItNum(contoForm.imponibile),
        iva_pct: parseFloat(contoForm.iva_pct) || 0,
        stato: 'bozza',
        note: `Generato da ${contoBlock.label} (${contoBlock.n_documenti} documenti)`,
        riepilogo_snapshot: { acconto_riferimento: contoBlock.riferimento, documenti: contoBlock.documenti, totale: contoBlock.totale },
      }, { withCredentials: true });
      toast.success(`Conto ${r.data.numero || ''} creato da ${contoBlock.label}`);
      setContoDialogOpen(false);
      onContoCreato?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore creazione conto');
    } finally {
      setCreatingConto(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { blocchi, non_assegnati: nonAssegnati, resoconto } = data;

  return (
    <div className="space-y-5 mt-4" data-testid="acconti-riepilogo">
      {/* Resoconto generale */}
      <Card className="border-slate-300 border-2">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Resoconto generale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-900 rounded-lg" data-testid="resoconto-totale-generale">
              <p className="text-xs text-slate-300">Totale generale (acconti + extra)</p>
              <p className="text-2xl font-bold text-white">{fmt(resoconto.totale_generale)} €</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="resoconto-totale-acconti">
              <p className="text-xs text-blue-600">Totale acconti ({resoconto.n_acconti})</p>
              <p className="text-2xl font-bold text-blue-700">{fmt(resoconto.totale_acconti)} €</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid="resoconto-totale-extra">
              <p className="text-xs text-orange-600">Totale extra (fuori preventivo)</p>
              <p className="text-2xl font-bold text-orange-700">{fmt(resoconto.totale_extra)} €</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="resoconto-n-documenti">
              <p className="text-xs text-emerald-600">Documenti contabilizzati</p>
              <p className="text-2xl font-bold text-emerald-700">{resoconto.n_documenti_totali}</p>
              {resoconto.n_non_assegnati > 0 && <p className="text-[11px] text-amber-600">+{resoconto.n_non_assegnati} da assegnare</p>}
            </div>
          </div>
          {resoconto.totali_per_blocco?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {resoconto.totali_per_blocco.map(t => (
                <Badge key={t.riferimento} variant="outline" className={String(t.riferimento || '').startsWith('extra') ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-slate-50 text-slate-700'}>
                  {t.label}: {fmt(t.totale)} € · {t.n_documenti} doc
                </Badge>
              ))}
            </div>
          )}
          {resoconto.ricarico_materiali_pct > 0 && (
            <p className="text-[11px] text-slate-400 mt-2">Importi lato cliente: manodopera a prezzo cliente + materiali DDT con ricarico {resoconto.ricarico_materiali_pct}%</p>
          )}
        </CardContent>
      </Card>

      {blocchi.length === 0 && nonAssegnati.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Euro className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>Nessun documento per questo cantiere.</p>
          <p className="text-sm">Approva rapportini o crea DDT indicando l'acconto di riferimento.</p>
        </div>
      )}

      {/* Blocchi acconto + extra */}
      {blocchi.map(b => {
        const isExtra = String(b.riferimento || '').startsWith('extra');
        const busy = regenerating.has(b.riferimento);
        return (
          <Card key={b.riferimento} className={isExtra ? 'border-orange-300' : 'border-blue-200'} data-testid={`blocco-acconto-${b.riferimento}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <h3 className={`text-lg font-bold ${isExtra ? 'text-orange-700' : 'text-blue-800'}`}>{b.label}</h3>
                  <Badge variant="outline">{b.n_documenti} documenti</Badge>
                  <span className="text-xl font-bold text-slate-900">{fmt(b.totale)} €</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => regenerateBlock(b.riferimento)} data-testid={`rigenera-ai-${b.riferimento}`}>
                    {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Rigenera AI
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openContoDialog(b)} data-testid={`crea-conto-${b.riferimento}`}>
                    <FileSignature className="h-4 w-4 mr-1.5" />Crea Conto
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 text-sm text-slate-500">
                <span>Manodopera: <span className="font-semibold text-slate-700">{fmt(b.totale_manodopera)} €</span></span>
                <span>Materiali (IVA incl.): <span className="font-semibold text-slate-700">{fmt(b.totale_materiali)} €</span></span>
              </div>

              {/* Ricarico del blocco (iter71) */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`ricarico-blocco-${b.riferimento}`}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Ricarico del blocco</p>
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="space-y-1">
                    <Label className="text-xs">Manodopera %</Label>
                    <Input
                      className="w-24 h-8 bg-white"
                      value={(ricEdits[b.riferimento]?.mano) ?? ''}
                      onChange={e => setRicEdits(prev => ({ ...prev, [b.riferimento]: { ...prev[b.riferimento], mano: e.target.value } }))}
                      placeholder="0"
                      data-testid={`ricarico-mano-${b.riferimento}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Materiali %</Label>
                    <Input
                      className="w-24 h-8 bg-white"
                      value={(ricEdits[b.riferimento]?.mat) ?? ''}
                      onChange={e => setRicEdits(prev => ({ ...prev, [b.riferimento]: { ...prev[b.riferimento], mat: e.target.value } }))}
                      placeholder="0"
                      data-testid={`ricarico-mat-${b.riferimento}`}
                    />
                  </div>
                  <Button size="sm" variant="outline" className="h-8" disabled={ricBusy.has(b.riferimento)} onClick={() => saveRicarico(b)} data-testid={`ricarico-applica-${b.riferimento}`}>
                    {ricBusy.has(b.riferimento) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Applica'}
                  </Button>
                  {b.totale_base !== b.totale && (
                    <p className="text-xs text-slate-500 ml-auto" data-testid={`ricarico-riepilogo-${b.riferimento}`}>
                      Base: <span className="font-semibold">{fmt(b.totale_base)} €</span> → con ricarico: <span className="font-semibold text-blue-700">{fmt(b.totale)} €</span>
                    </p>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Deseleziona "Ric." nella tabella per escludere un documento dal ricarico.</p>
              </div>

              {/* Descrizione AI */}
              <div className={`p-3 rounded-lg border ${isExtra ? 'bg-orange-50 border-orange-200' : 'bg-indigo-50 border-indigo-200'}`} data-testid={`descrizione-ai-${b.riferimento}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className={`h-4 w-4 ${isExtra ? 'text-orange-600' : 'text-indigo-600'}`} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descrizione conto (AI)</p>
                  {busy && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />aggiornamento in corso…</span>}
                </div>
                {b.oggetto_ai && <p className="text-sm font-semibold text-slate-800">{b.oggetto_ai}</p>}
                {b.descrizione_ai
                  ? <p className="text-sm text-slate-700 whitespace-pre-line mt-1">{b.descrizione_ai}</p>
                  : !busy && <p className="text-sm text-slate-400 italic">Descrizione non ancora generata.</p>}
                {b.ai_updated_at && !busy && <p className="text-[10px] text-slate-400 mt-1">Aggiornata: {b.ai_updated_at.slice(0, 16).replace('T', ' ')}</p>}
              </div>

              {/* Documenti */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-500">Tipo</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Numero</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Data</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Descrizione</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500">Dettaglio</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 text-center" title="Applica ricarico a questo documento">Ric.</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 text-right">Importo</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {b.documenti.map(d => (
                    <TableRow key={`${d.tipo}-${d.id}`}>
                      <TableCell><TipoBadge tipo={d.tipo} /></TableCell>
                      <TableCell className="font-medium">{d.numero}</TableCell>
                      <TableCell className="text-slate-500">{d.data?.slice(0, 10)}</TableCell>
                      <TableCell className="max-w-[280px] truncate text-slate-600" title={d.descrizione}>{d.descrizione}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{d.dettaglio}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox checked={!d.escluso_ricarico} onCheckedChange={() => toggleEscluso(b, d)} data-testid={`ricarico-doc-${d.id}`} />
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmt(d.importo)} €
                        {d.ricarico_pct_applicato > 0 && (
                          <p className="text-[10px] text-slate-400 font-normal">base {fmt(d.importo_base)} € +{d.ricarico_pct_applicato}%</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" title="Rimuovi dal blocco" onClick={() => unassignDoc(d.tipo, d.id)} data-testid={`rimuovi-doc-${d.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {/* Non assegnati */}
      {nonAssegnati.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40" data-testid="non-assegnati-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-bold text-amber-800">Documenti da assegnare ({nonAssegnati.length})</h3>
              </div>
              <div className="flex items-center gap-2">
                <AccontoSelector compact value={assignTarget} onChange={setAssignTarget} dataTestId="assegna-acconto-select" />
                <Button size="sm" disabled={assignBusy || selectedDocs.size === 0 || !assignTarget} onClick={assignSelected} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="assegna-selezionati-btn">
                  {assignBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Assegna {selectedDocs.size > 0 ? `(${selectedDocs.size})` : ''}
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedDocs.size === nonAssegnati.length && nonAssegnati.length > 0}
                      onCheckedChange={(v) => setSelectedDocs(v ? new Set(nonAssegnati.map(d => `${d.tipo}|${d.id}`)) : new Set())}
                      data-testid="seleziona-tutti-checkbox"
                    />
                  </TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Numero</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Data</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Descrizione</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Importo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nonAssegnati.map(d => (
                  <TableRow key={`${d.tipo}-${d.id}`} className="cursor-pointer" onClick={() => toggleDoc(d.tipo, d.id)}>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Checkbox checked={selectedDocs.has(`${d.tipo}|${d.id}`)} onCheckedChange={() => toggleDoc(d.tipo, d.id)} data-testid={`doc-checkbox-${d.id}`} />
                    </TableCell>
                    <TableCell><TipoBadge tipo={d.tipo} /></TableCell>
                    <TableCell className="font-medium">{d.numero}</TableCell>
                    <TableCell className="text-slate-500">{d.data?.slice(0, 10)}</TableCell>
                    <TableCell className="max-w-[320px] truncate text-slate-600" title={d.descrizione}>{d.descrizione}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(d.importo)} €</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog Crea Conto da blocco */}
      <Dialog open={contoDialogOpen} onOpenChange={setContoDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Crea Conto da {contoBlock?.label}</DialogTitle>
            <DialogDescription>Il conto verrà creato nella sezione "Conti emessi" con la descrizione AI del blocco.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Descrizione</Label>
              <Textarea rows={7} value={contoForm.descrizione} onChange={e => setContoForm({ ...contoForm, descrizione: e.target.value })} data-testid="conto-acconto-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Imponibile (€)</Label>
                <Input value={contoForm.imponibile} onChange={e => setContoForm({ ...contoForm, imponibile: e.target.value })} data-testid="conto-acconto-imponibile" />
                <p className="text-[11px] text-slate-400">Calcolato dal blocco: {fmt(contoBlock?.totale)} €</p>
              </div>
              <div className="space-y-1">
                <Label>IVA %</Label>
                <Select value={contoForm.iva_pct} onValueChange={v => setContoForm({ ...contoForm, iva_pct: v })}>
                  <SelectTrigger data-testid="conto-acconto-iva"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (esente / split payment)</SelectItem>
                    <SelectItem value="4">4%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="22">22%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setContoDialogOpen(false)}>Annulla</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={creatingConto} onClick={handleCreaConto} data-testid="conferma-crea-conto-btn">
                {creatingConto ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSignature className="h-4 w-4 mr-1.5" />}Crea Conto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
